<template>
  <div id="SubscriptionWrap">
    <div class="paymentModal loading" v-if="isLoading">
      <mu-content-block style="text-align: center">
        <mu-circular-progress :size="32"></mu-circular-progress>
        <div v-text="$t('message.loadingText')"></div>
      </mu-content-block>
    </div>
    <div class="modal" v-if="!isLoading">
      <div class="block payment" v-if="!isPaymentCancelled && !hasPaymentProcessed">
        <mu-row :gutter="true">
          <mu-col desktop="55" tablet="55" width="100">
            <div class="choose-plan">
              <h2>Choose your plan</h2>
              <p>Get access to the world's largest low carb community, multi-award winning education, daily meal plans,
                recipes and more!</p>
              <div class="discount-code">
                <h3>Got a discount code?</h3>
                <p>Choose your plan, then enter your code at checkout.</p>
              </div>
              <div class="payment-plans" v-if="discountData">
                <div v-for="(price, index) in prices">
                  <div class="subscription"
                       :key="`price-${index}`" @click="createStripeSession(price)"
                       :class="{
                     'yearly': price.description === 'Yearly',
                     'monthly': price.description !== 'Yearly'
                   }"
                  >
                    <div class="overlay" @click.native="createStripeSession(price)">
                      <mu-icon class="radio unchecked" v-show="!price.selected" value="radio_button_unchecked"/>
                      <mu-icon class="radio checked" v-show="price.selected" value="radio_button_checked"/>
                      <strong class="itemName" v-text="price.description"></strong>
                      <span class="itemPrice">
                  {{price.amount}}
                </span>
                      <span class="itemDesc" v-html="price.extra ? price.extra : price.extra_description"></span>
                      <div v-if="price.percentageOffForCurrentUser > 0" class="discount" >
                        <span>Save {{price.percentageOffForCurrentUser}}%</span>
                      </div>

                    </div>
                  </div>
                </div>
                <stripe-checkout
                  ref="sessionCheckoutRef"
                  :pk="publishableKey"
                  :session-id="sessionId"
                />
              </div>
              <div class="terms">
                <p>Recurring billing, cancel anytime.</p>
                <p>By continuing, you accept our <a :href="Config('siteUrl') + 'terms'" target="_blank">Terms and
                  Conditions</a>, <a :href="Config('siteUrl') + 'privacy'" target="_blank">Privacy Policy</a> and <a
                  :href="Config('siteUrl') + 'billing-terms'" target="_blank">Billing Terms</a></p>
                <div class="processing" v-if="selectedPackage">
                  <p>Processing...</p>
                </div>
              </div>
            </div>
          </mu-col>
          <mu-col desktop="45" tablet="45" width="100">
            <h2 class="whySub">Why subscribe?</h2>
            <div class="benefits">
              <ul>
                <li><span class="material-icons">check</span>Award-winning, proven education on how to embrace a low carb lifestyle</li>
                <li><span class="material-icons">check</span>Practical Action Points to achieve your health goals</li>
                <li><span class="material-icons">check</span>Daily meal plans for 12 weeks to start and sustain your journey</li>
                <li><span class="material-icons">check</span>Over 1,800 searchable recipes and meal plans tailored to your preferences</li>
                <li><span class="material-icons">check</span>Group and one-to-one virtual meetups with expert coaches</li>
                <li><span class="material-icons">check</span>Peer support from {{numeral(stats.count).format('0,0')}} members</li>
                <li><span class="material-icons">check</span>Integrated food diary with food search and barcode scanner</li>
                <li><span class="material-icons">check</span>Integrated health tracking and data exporting</li>
                <li><span class="material-icons">check</span>Daily updated news, research and guidance</li>
                <li><span class="material-icons">check</span>Access on the web, iOS, Android and Alexa</li>
                <li><span class="material-icons">check</span>Sync your data with Apple Health, Google Fit and FitBit</li>
                <li><span class="material-icons">check</span>Regularly updated features and enhancements</li>
                <li><span class="material-icons">check</span>Access on Apple Watch and Android Watch</li>
                <li><span class="material-icons">check</span>Friendly technical support</li>
              </ul>
            </div>
          </mu-col>
        </mu-row>
      </div>
      <div class="block cancelled" v-if="isPaymentCancelled">
        <h1>{{$t('message.paywallPaywallFailed')}}</h1>
        <p>{{$t('message.paywallPaywallFailedExplain')}}</p>
        <mu-flat-button type="button" class="button" @click="goTo('Subscription')" label="Try again"/>
      </div>
      <div class="block success" v-if="hasPaymentProcessed">
        <table class="table">
          <tbody>
          <tr>
            <td>Package</td>
            <td>
              {{ getParam('item_name') }}
            </td>
          </tr>
          <tr>
            <td>Subscription ID</td>
            <td>
              {{ getParam('subscr_id') }}
            </td>
          </tr>
          <tr v-if="getParam('trialbillingperiod')">
            <td>
              Trial period
            </td>
            <td>{{getParam('trialbillingperiod')}}</td>
          </tr>
          <tr v-if="getParam('trialamt')">
            <td>Trial price</td>
            <td>{{ getParam('trialamt') }} {{ getParam('cc') }}</td>
          </tr>
          <tr>
            <td>Price</td>
            <td>{{ getParam('amt') }} {{ getParam('cc') }}</td>
          </tr>
          </tbody>
        </table>
        <mu-flat-button
          type="button"
          class="button"
          @click.native="continueProgram()"
          label="Get started"
          fullWidth
        />
      </div>
      <div class="titleLink">
        <router-link
          :to="'/register/amend'"
        >Need to update your details?
        </router-link>
      </div>
    </div>
    <div class="modal" v-if="!isLoading">
      <h2>Been referred by your healthcare team?</h2>
      <p>Enter your referral code below to access the Low Carb Program.</p>
      <form id="voucherForm" @submit.prevent="processVoucher" novalidate>
        <div id="voucher">
          <mu-row :gutter="true">
            <mu-col tablet="70" desktop="70" width="70">
              <mu-text-field id="voucherTxt" hintText="Referral code (this is different from a discount code)" v-model="voucher.code" fullWidth :min="20"/>
            </mu-col>
            <mu-col tablet="30" desktop="30" width="30">
              <!-- Firefox fix -->
              <button type="submit" style="display: none"></button>
              <mu-flat-button id="submitBtn" @click.native="processVoucher" type="button" label="Check code"
                              class="apply" :disabled="(voucher.code.length <= 3) || voucher.isSending"
                              color="#FFF" fullWidth/>
            </mu-col>
          </mu-row>
        </div>
        <div class="message" :class="{ error : voucher.error === true, success : voucher.error === false }"
             v-text="voucher.isSending ? 'Processing...' : voucher.message"></div>
      </form>
      <p class="discount-text">Got a discount code? Select a plan above and enter it at checkout</p>
    </div>
  </div>
</template>

<script>

  import PaymentApi from '../../api/Payment/index';
  import Config from '../../config';
  import axios from 'axios';
  import numeral from 'numeral';
  import _ from "lodash";
  import AuthenticationManager from "../../helpers/AuthenticationManager";
  import {sha256} from "js-sha256";
  import { StripeCheckout } from '@vue-stripe/vue-stripe';
  import UserApi from '../../api/User';

  export default {
    name: 'subscription-form',
    components: {
      StripeCheckout,
    },
    data() {
      return {
        stats: {},
        isLoading: true,
        app_url: null,
        subscription: {},
        selectedPackage: false,
        voucher: {
          code: "",
          error: false,
          message: "",
          isSending: false,
        },
        fbPrice: null,
        loading: false,
        prices: [],
        sessionId: null,
        publishableKey: `${Config.stripePublishableKey}`,
        discountData: null
      }

    },
    methods: {

      createStripeSession(price) {
        if (this.selectedPackage) {
          return;
        }
        this.selectedPackage = true;
        price.selected = true;
        self.fbPrice = price.amount;
        PaymentApi.initalizePayment({price: price.id, nickname: price.description}).then((res) => {
          this.sessionId = res.data.data.session.session_id;
          this.$refs.sessionCheckoutRef.redirectToCheckout();
        }).catch((e) => {
          alert("Payment not possible at this time, Please try again later");
        }).finally(() => {
          price.selected = false;
          this.selectedPackage = false;
        })
      },
      activateStripePlan(price){
        price.selected = true;
        const priceRef = `${price.id}checkoutRef`
        this.$refs[priceRef][0].redirectToCheckout();
      },
      numeral(num) {
        return numeral(num);
      },
      getStats() {
        let self = this;
        axios.get(Config.apiUrl + 'stats').then(response => {
          self.stats = response.data;
        });
      },
      getDiscount() {
        let self = this;
        axios.get(Config.apiUrl + `discount/current/user/`)
          .then(function (response) {
            self.discountData = response.data.data;
          })
      },
      continueProgram() {
        let self = this;
        if ((self.$store.state.status.onboarding.about) || (self.$store.state.status.onboarding.location) || (self.$store.state.status.goals) || (self.$store.state.status.personalise)) {
          self.goTo('Dashboard');
        } else {
          self.goTo('GettingStartedAboutYourself');
        }
      },
      processVoucher() {
        let self = this;
        self.voucher.error = false;
        self.voucher.isSending = true;
        PaymentApi.validateVoucher({
          voucher: self.voucher.code.toUpperCase().trim()
        }).then(response => {
          if (response.data.error === false) {
            let trackedVouchers = [
              'EASYHEALTH',
              'EASYHEALTH42',
              'EASYHEALTH43',
              'COMMUNITY',
              'HURRY',
              'RESEARCH51',
              'RESEARCH52',
              'RESEARCH53',
              'RESEARCH54',
              'RESEARCH55',
              'RESEARCH56',
              'LOWCARBNOW'
            ];

            let containsTrackable = (trackedVouchers.indexOf(self.voucher.code.toUpperCase().trim()) > -1);
            if (containsTrackable) {
              self.$gtag.event(self.voucher.code.toUpperCase().trim(), {
                'event_category': 'Vouchers',
                'event_label': 'Redeemed voucher code',
                'value': 1
              })
            }

            let fbVoucherModel = {
              "data": [
                {
                  "event_name": "RedeemedVoucher",
                  "event_time": moment().unix(),
                  "action_source": "email",
                  "user_data": {
                    "em": sha256(self.$store.state.user.scope.email),
                    "client_ip_address": self.$store.state.geoLocation.host.ip,
                    "client_user_agent": navigator.userAgent
                  },
                  "custom_data": {
                    "voucher": self.voucher.code
                  }
                }
              ]
            }
            axios.post('https://graph.facebook.com/v11.0/625160948457652/events?access_token=EAALxgEaGHOQBAPhSgGaAn8wfcfGUmKNrCk6cpoQO7NX85wxgmaD56HgMmGQLUEKMPsyCAFgMuh33vl8r1go8uO1xHgCoM3y9Ggkm2JejlIX6jeoCEW3GnA9V9N6FMInLHjSh9lzKmaEAIacBfG0o4NUqUcdolpi4frbZCh8VQU1qPtpK30wSj3VbxTKQZD', fbVoucherModel)
              .then((res) => {
              })
              .catch((res) => {
              })


            self.$gtag.event( 'VoucherRedemption', {
              'event_category': 'Registration',
              'event_label': 'Redeemed a voucher code',
              'value': 1
            });
            self.voucher.isSending = false;
            self.voucher.error = false;
            self.voucher.message = `${response.data.message}. Redirecting...`;
            // redirect to getting started
            self.goToGetStarted();
          }
        }, (error) => {
          self.$gtag.event('VoucherRedemption', {
            'event_category': 'Registration',
            'event_label': 'Redeem voucher failed',
            'value': 0
          });
          let callback = error.response.data;
          if (callback.error) {
            self.voucher.isSending = false;
            self.voucher.error = true;
            self.voucher.message = callback.message;
            self.voucher.code = "";
          }
        });
      },
      goToGetStarted() {
        let self = this;
        if (AuthenticationManager.hasToken()) {
          const tokenObject = AuthenticationManager.getToken();
          AuthenticationManager.refreshAccessToken(tokenObject).then(() => {
            if (_.includes(AuthenticationManager.parseJwt().scope, "standard_tasks")) {
              if ((self.$store.state.status.onboarding.about) || (self.$store.state.status.onboarding.location) || (self.$store.state.status.goals) || (self.$store.state.status.personalise)) {
                self.goTo('Dashboard');
              } else {
                self.goTo('GettingStartedAboutYourself');
              }
            } else {
              self.$toastr.error('Having trouble getting started. Please email support@lowcarbprogram.com');
            }
          });
        }
      },
      activatePlan(p) {
        let self = this;
        if (self.selectedPackage) {
          return;
        }
        self.selectedPackage = true;
        p.selected = true;
        self.fbPrice = p.price;

        self.$forceUpdate();
        PaymentApi.getUuid().then(response => {
          const uuid = response.data.uuid;
          if (!uuid.length) {
            throw "uuid cannot be blank";
          }
          setTimeout(() => {
            window.location.href = self.Config('apiUrl') + `paypal/subscription/express-checkout?package=${p.key}&uuid=${uuid}`;
          }, 800);
        }).catch(error => {
          //todo handle this
        });
      },
      getParam(param) {
        let self = this;
        return self.$route.query[param];
      },
      Config(key) {
        return Config[key];
      },
      getDetails() {
        let self = this;
        PaymentApi.getPayPalSubscriptionDetails().then(response => {
          self.subscription = response.data;

          let packages = response.data.packages;

          if (!this.$store.state.user.wasAvivaEligible) {
            delete packages['aviva-yearly-discount'];
          } else {
            delete packages['yearly'];
          }

          self.subscription.packages = packages;
          // self.isLoading = false;
        });
      },
      async getUser() {
        let self = this;
        let response = await UserApi.getUser();
        self.$store.commit('setUser', response.data);
      },
      initMounted() {
        let self = this;
        self.getDiscount();
        self.getStripePrices();
        let siteURL = '';
        if (Config.ApplicationContext === 'lowcarbprogram') {
          siteURL = 'https://www.lowcarbprogram.com/';
        } else {
          siteURL = 'https://www.grohealth.com/';
        }
        self.app_url = siteURL + Config.locale + '/';
        self.getDetails();
        self.getStats();
        self.getUserPaidStatus();

        // Handle successful payment
        if (self.getParam('success') == 1) {

          // Required to gaurantee that the user is logged in and email is available
          this.getUser()
            .then(() => {

              // Google Analytics event for successful payment
              self.$gtag.event({
                'event_category': 'Registration',
                'event_label': 'PayPal',
                'event_action': 'Subscription',
                'event_value': 1
              });

              // Parse payment details
              let planName = self.getParam('item_name');
              let amount = self.getParam('amt');
              let currency = self.getParam('cc');

              // Wrap pixel request in try catch loop to prevent looping
              try {
                // Google Ads conversion for successful payment
                self.$gtag.query('event', 'conversion', {
                  'send_to': planName.toLowerCase().includes('monthly')
                    ? 'AW-1020622366/_hvYCN3Y_sADEJ7s1eYD'
                    : 'AW-1020622366/siWpCNrY_sADEJ7s1eYD',
                  'transaction_id': self.getParam('subscr_id')
                });

                // Generate facebook pixel event data
                let fbPaidModel = {
                  "data": [
                    {
                      "event_name": planName.toLowerCase().includes('monthly')
                        ? "MonthlySubscription"
                        : "YearlySubscription",
                      "event_time": moment().unix(),
                      "action_source": "email",
                      "user_data": {
                        "em": sha256(self.$store.state.user.scope.email),
                        "client_ip_address": self.$store.state.geoLocation.host.ip,
                        "client_user_agent": navigator.userAgent
                      },
                      "custom_data": {
                        "currency": currency,
                        "value": Number(amount),
                        "plan": planName
                      }
                    }
                  ]
                };

                // Send facebook pixel event data
                axios.post('https://graph.facebook.com/v11.0/625160948457652/events?access_token=EAALxgEaGHOQBAPhSgGaAn8wfcfGUmKNrCk6cpoQO7NX85wxgmaD56HgMmGQLUEKMPsyCAFgMuh33vl8r1go8uO1xHgCoM3y9Ggkm2JejlIX6jeoCEW3GnA9V9N6FMInLHjSh9lzKmaEAIacBfG0o4NUqUcdolpi4frbZCh8VQU1qPtpK30wSj3VbxTKQZD', fbPaidModel)
                  .then((res) => {
                  })
                  .catch((res) => {
                  })
              } catch (e) {
              }
            })
        }
      },
      getStripePrices(){
        PaymentApi.getStripePrices().then((res) => {
          this.prices = res.data.data.prices;
        })
      },
      getUserPaidStatus() {
        let self = this;
        if (AuthenticationManager.hasToken()) {
          const tokenObject = AuthenticationManager.getToken();
          AuthenticationManager.refreshAccessToken(tokenObject).then(() => {

            if (_.includes(AuthenticationManager.parseJwt().scope, "standard_tasks")) {
              self.isLoading = false;
              self.$store.commit('setPaymentStatus', true);
              if( (self.$store.state.status.onboarding.about) || (self.$store.state.status.onboarding.location) || (self.$store.state.status.goals) || (self.$store.state.status.personalise) ) {
                self.goTo('Dashboard');
              } else {
                self.goTo('GettingStartedAboutYourself');
              }

            } else {
              self.isLoading = false;
            }
          });
        } else {
          self.isLoading = false;
        }
      },
      goTo(name) {
        let self = this;
        self.$router.push({
          name: name,
        });
      },
    },
    computed: {
      hasPaymentProcessed() {
        let self = this;
        return self.getParam('success');
      },
      isPaymentCancelled() {
        let self = this;
        return self.getParam('cancelled');
      },
    },
    mounted() {
      let self = this;
      self.initMounted();
    }
  }

</script>

<style lang="less">
  #Subscription {
    text-align: center;

    a {
      text-decoration: underline;
    }

    p {
      font-weight: 500;
    }

    .sub-notice {
      font-size: 14px;
      font-weight: 300;
      padding: 10px 16px 10px 16px;
      background-color: #6ca02c;
      border-radius: 0 0 4px 4px;
      width: 100%;
      left: 0;
      background-repeat: no-repeat;
      background-position-y: 11px;
      background-position-x: 13px;
      text-align: center;
      background-size: 25px;
      color: white;
    }

    h1, h2 {
      margin: 0 0 8px 0;
      font-size: 25px;
      color: #0179d5;
      font-weight: 700;

      &.whySub {
        margin-bottom: .5em;
        margin-top: .5em;
      }
    }

    .modal {
      display: inline-block;
      max-width: 1024px;
      width: 100%;
      margin: 30px 0 0;
      padding: 24px;
      text-align: left;
      border-radius: 8px;
      background-color: #FFF;
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);


      .choose-plan {
        padding: 16px 24px 20px;
        border-radius: 12px;
        border: solid 1px #c5e3ff;

        .discount-code {
          padding: 15px 20px;
          border-radius: 8px;
          background-color: #ffebc5;
          h3 {
            margin: 0 0 8px;
            font-size: 1rem;
          }
          p {
            margin: 0;
          }
        }

        .terms {
          padding: 0 10px;
        }

        .payment-plans {
          margin-bottom: 1rem;
          display: flex;
          flex-direction: column-reverse;
        }
      }

      .discount-text {
        color: #9c9c9c;
      }
      h3 {
        margin: 10px 0 25px;
      }

      .benefits {
        margin: 0 0 30px;
        overflow: hidden;

        ul {
          padding: 0;
          margin: 0;
          list-style-type: none;
          text-align: left;

          li {
            padding: 0 0 0 35px;
            position: relative;
            font-weight: 500;

            span {
              display: inline;
              color: #89C93B;
              position: absolute;
              top: -2px;
              left: 0;
            }
          }
        }
      }

      > .block > .row > .col {
        padding: 0 15px;
      }

      .subscription {
        padding: 15px 70px 15px 70px;
        margin: 17px 0 0;
        color: #FFF;
        border-radius: 8px;
        background: #0179d5;
        position: relative;
        cursor: pointer;

        input {
          width: 100%;
          height: 100%;
          opacity: 0;
          filter: alpha(opacity=0);
          -ms-filter: alpha(opacity=0);
          position: absolute;
          top: 0;
          left: 0;
          cursor: pointer;
        }

        .radio {
          font-size: 26px;
          margin-top: -13px;
          position: absolute;
          left: 24px;
          top: 50%;
        }

        .itemDesc {
          display: block;
          margin: 3px 0 0;
          font-weight: 500;
        }

        .itemPrice {
          margin: -14px 0 0;
          font-size: 19px;
          font-weight: 500;
          position: absolute;
          top: 50%;
          right: 24px;
        }

        &.yearly {
          padding: 15px 70px 15px 72px;
          .itemPrice {
            top: 50%;
          }

          .unchecked {
            background: #FFF;
            border-radius: 60%;
            font-size: 22px;
            left: 26px;
            top: 53%;
          }
        }


        &.monthly {
          background: #FFF;
          color: #000;
          border: 2px solid #0179d5;

          .radio {
            color: #0179d5;
          }
          .itemPrice {
            top: 50%;
          }
        }

        .discount {
          padding: 2px 5px;
          border-radius: 4px;
          background-color: #d86f38;
          font-size: 13px;
          position: absolute;
          top: 50%;
          right: 21px;
          font-weight: 300;
        }
      }

      .processing {
        margin: 24px 0 0;
        text-align: center;
      }
    }

    .cancelled {
      text-align: center;
    }

    .success {
      .table {
        width: 100%;
        margin: 20px 0 35px;
        text-align: left;
        font-size: 16px;
        font-weight: 300;
        border-spacing: 0;

        tr {
          td {
            padding: 11px 5px;
            color: #777;
            text-align: right;
            border-bottom: 1px solid #ECECEC;

            &:first-child {
              text-align: left;
              color: #000000;
            }

            .keepsafe {
              color: #ABABAB;
              font-size: 13px;
              font-style: italic;
              display: block;
            }
          }

          &:hover {
            td {
              border-color: #DADADA;
            }
          }
        }
      }
    }

    .seperator {
      height: 40px;
      margin: 13px 0;
      position: relative;

      &:before {
        display: block;
        width: 100%;
        height: 2px;
        content: '';
        background: fade(#EEE, 80%);
        position: absolute;
        top: 50%;
        left: 0;
      }

      .overlay {
        width: 100%;
        height: 100%;
        text-align: center;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 9;

        span {
          display: inline-block;
          height: 100%;
          line-height: 40px;
          padding: 0 20px;
          color: #bababa;
          font-weight: 400;
          text-transform: uppercase;
          background-color: #FFF;
          position: relative;
          z-index: 9;
          font-size: 20px;
        }
      }
    }

    .button {
      width: 100%;
      display: block;
      height: 56px;
      line-height: normal;
      margin: 24px 0 0;
      color: #FFF;
      font-size: 16px;
      font-weight: 600;
      border: 0;
      outline: 0;
      border-radius: 4px;
      border: 1px solid #2B91C4;
      background-color: #38A1F1;
      text-transform: none;
      cursor: pointer;
      text-decoration: none;
      -webkit-transition: background linear 150ms;
      -moz-transition: background linear 150ms;
      -o-transition: background linear 150ms;
      -ms-transition: background linear 150ms;
      transition: background linear 150ms;

      &.free {
        color: #FFF;
        border: 1px solid #75B030;
        background: #8BC34A;

        &:hover {
          background: #75B030;
        }
      }

      .mu-flat-button-label {
        font-size: 16px;
        color: #FFF;
      }

      &:hover {
        background-color: #1C89DC;
        -webkit-transition: background linear 150ms;
        -moz-transition: background linear 150ms;
        -o-transition: background linear 150ms;
        -ms-transition: background linear 150ms;
        transition: background linear 150ms;
      }

      &:active {
        position: relative;
        top: 1px;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }
  }

  #voucherForm {
    margin: 0px 0px 0px 0px;

    #voucher {
      position: relative;
      text-align: left;

      input {
        font-weight: 300;
        color: #212121;
        letter-spacing: 0;
        text-transform: uppercase;
      }

      .apply {
        width: 100%;
        min-width: initial;
        font-weight: 600;
        text-transform: none;
        background-color: #8BC34A;
        border-radius: 4px;

        .mu-flat-button-label {
          padding: 0;
        }

        &.disabled {
          background-color: #CCCCCC;
          opacity: .8;
        }
      }
    }

    .message {
      font-size: 13px;
      text-align: center;

      &.error {
        color: red;
      }

      &.success {
        color: #8BC34A;
      }
    }
  }

  #SubscriptionWrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    .titleLink {
      text-align: center;
      padding-top: 10px;
    }
  }

</style>



// WEBPACK FOOTER //
// SubscriptionForm.vue?f7548af2